import {useContext, useEffect, useState} from 'react'
import {SELECT_WIRE, WireContext} from '../context/context'
import SelectInput from '../../common/SelectInput'
import InputText from '../../common/InputText'
import TextArea from '../../common/TextArea'
import InlineCheckbox from '../../common/InlineCheckbox'
import Button from '../../common/Button'
import useDebouncedValue from '../../../utils/common_function'
import {useParams} from 'react-router-dom'
import {getRequest, postRequest, putRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import {Card, Col, Flex, Form, Modal, Row, Typography} from 'antd'
import {useIntl} from 'react-intl'

const WireFormModal = (props) => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(WireContext)
  const [, navDispatch] = useContext(NavContext)

  const [form] = Form.useForm()

  // Options state for each async select
  // Keyed per field: six dropdowns share this form, and only the one actually
  // searched should say the search failed.
  const [searchFailed, setSearchFailed] = useState({})
  const [harnessOptions, setHarnessOptions] = useState([])
  const [connectorFromOptions, setConnectorFromOptions] = useState([])
  const [connectorToOptions, setConnectorToOptions] = useState([])
  const [colorOptions, setColorOptions] = useState([])
  const [circuitOptions, setCircuitOptions] = useState([])

  // Loading states for async selects (show spinner in dropdown while fetching)
  const [harnessLoading, setHarnessLoading] = useState(false)
  const [connectorFromLoading, setConnectorFromLoading] = useState(false)
  const [connectorToLoading, setConnectorToLoading] = useState(false)
  const [colorLoading, setColorLoading] = useState(false)
  const [circuitLoading, setCircuitLoading] = useState(false)

  // Search states for debounced async fetching
  const [searchHarness, setSearchHarness] = useState('')
  const [searchConnectorFrom, setSearchConnectorFrom] = useState('')
  const [searchConnectorTo, setSearchConnectorTo] = useState('')
  const [searchColor, setSearchColor] = useState('')
  const [searchCircuit, setSearchCircuit] = useState('')

  const debouncedHarness = useDebouncedValue(searchHarness, 500)
  const debouncedConnectorFrom = useDebouncedValue(searchConnectorFrom, 500)
  const debouncedConnectorTo = useDebouncedValue(searchConnectorTo, 500)
  const debouncedColor = useDebouncedValue(searchColor, 500)
  const debouncedCircuit = useDebouncedValue(searchCircuit, 500)

  // Initial fetch for all options on modal open
  useEffect(() => {
    if (!state.show_wire_popup) return

    fetchHarnessOptions('')
    fetchConnectorOptions('', 'connector_from')
    fetchConnectorOptions('', 'connector_to')
    fetchColorOptions('')
    fetchCircuitOptions('')
  }, [state.show_wire_popup])

  // Debounced search fetches
  useEffect(() => {
    if (debouncedHarness !== undefined) fetchHarnessOptions(debouncedHarness)
  }, [debouncedHarness])

  useEffect(() => {
    if (debouncedConnectorFrom !== undefined)
      fetchConnectorOptions(debouncedConnectorFrom, 'connector_from')
  }, [debouncedConnectorFrom])

  useEffect(() => {
    if (debouncedConnectorTo !== undefined)
      fetchConnectorOptions(debouncedConnectorTo, 'connector_to')
  }, [debouncedConnectorTo])

  useEffect(() => {
    if (debouncedColor !== undefined) fetchColorOptions(debouncedColor)
  }, [debouncedColor])

  useEffect(() => {
    if (debouncedCircuit !== undefined) fetchCircuitOptions(debouncedCircuit)
  }, [debouncedCircuit])



  // Fetch functions
  const fetchHarnessOptions = async (search = '') => {
    setHarnessLoading(true)
    try {
      let res = await getRequest(
        `/api/harnesses/?vehicle=${vehicle}&search=${search}`
      )
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setSearchFailed((prev) => ({ ...prev, harness: true }))
        setHarnessOptions([])
        return
      }
      setSearchFailed((prev) => ({ ...prev, harness: false }))
      const options = (res.results || []).map((item) => ({
        value: item.id,
        label: item.harness_id,
        ...item,
      }))
      setHarnessOptions(options)
    } finally {
      setHarnessLoading(false)
    }
  }

  const fetchConnectorOptions = async (search = '', field = '') => {
    const setLoading = field === 'connector_from' ? setConnectorFromLoading : setConnectorToLoading
    const setOptions = field === 'connector_from' ? setConnectorFromOptions : setConnectorToOptions
    setLoading(true)
    try {
      let res = await getRequest(
        `/api/connector/?vehicle=${vehicle}&search=${search}`
      )
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setSearchFailed((prev) => ({ ...prev, [field]: true }))
        setOptions([])
        return
      }
      setSearchFailed((prev) => ({ ...prev, [field]: false }))
      const options = (res.results || []).map((item) => ({
        value: item.id,
        label: item.name,
        ...item,
      }))
      setOptions(options)
    } finally {
      setLoading(false)
    }
  }

  const fetchColorOptions = async (search = '') => {
    setColorLoading(true)
    try {
      let res = await getRequest(
        `/api/color/?vehicle=${vehicle}&search=${search}`
      )
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setSearchFailed((prev) => ({ ...prev, color: true }))
        setColorOptions([])
        return
      }
      setSearchFailed((prev) => ({ ...prev, color: false }))
      const options = (res.results || []).map((item) => ({
        value: item.id,
        label: item.name,
        ...item,
      }))
      setColorOptions(options)
    } finally {
      setColorLoading(false)
    }
  }

  const fetchCircuitOptions = async (search = '') => {
    setCircuitLoading(true)
    try {
      let res = await getRequest(
        `/api/circuit/options/?vehicle=${vehicle}&search=${search}&limit=50`
      )
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setSearchFailed((prev) => ({ ...prev, circuit: true }))
        setCircuitOptions([])
        return
      }
      setSearchFailed((prev) => ({ ...prev, circuit: false }))
      const options = (res.results || []).map((item) => ({
        value: item.id,
        label: item.name,
        ...item,
      }))
      setCircuitOptions(options)
    } finally {
      setCircuitLoading(false)
    }
  }



  const closeWireFormPopup = () => {
    dispatch({
      type: SELECT_WIRE,
      data: { selected_wire: {}, show_wire_popup: false },
    })
    form.resetFields()
  }

  const saveWire = async (values) => {
    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      // For select objects, send the id
      if (key === 'level_string') return
      if (value && typeof value === 'object' && value.value !== undefined) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    // Send level_string as comma-separated values, or empty string when blank
    const levelString = String(values.level_string || '')
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(',')
    formData.append('level_string', levelString)

    formData.append('vehicle', vehicle)

    // Handle checkbox values (they come as true/false from InlineCheckbox)
    if (values.internal === true) formData.append('internal', 'on')
    if (values.can_star === true) formData.append('can_star', 'on')

    // import_file hidden field
    formData.append(
      'import_file',
      state?.selected_wire?.import_file || ''
    )

    let res = null
    if (state?.selected_wire?.id) {
      res = await putRequest(
        `/api/wire/${state?.selected_wire?.id}/?vehicle=${vehicle}`,
        formData
      )
    } else {
      res = await postRequest(`/api/wire/?vehicle=${vehicle}`, formData)
    }

    if (res?.invalid_levels?.length > 0) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: `${res?.invalid_levels} ${intl.formatMessage({ id: 'wire.levelsNotExists' })}`,
        },
      })
    } else if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'wire.saveSuccess' }),
        },
      })
      props.getListData()
      closeWireFormPopup()
    } else {
      let msg = ''
      Object.keys(res).map((obj) => {
        if (msg) {
          msg += `, ${res[obj]}`
        } else {
          msg = `${res[obj]}`
        }
      })
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: msg,
        },
      })
    }
  }

  const getInitialValues = () => {
    const wire = state?.selected_wire || {}
    return {
      is_create: wire.id ? false : true,
      id: wire.id || null,
      import_file: wire.import_file || null,
      harness: wire.harness
        ? { value: wire.harness.id, label: wire.harness.harness_id, ...wire.harness }
        : null,
      connector_from: wire.connector_from
        ? { value: wire.connector_from.id, label: wire.connector_from.name, ...wire.connector_from }
        : null,
      connector_to: wire.connector_to
        ? { value: wire.connector_to.id, label: wire.connector_to.name, ...wire.connector_to }
        : null,
      option_code_expression: wire.option_code_expression || null,
      size: wire.size || null,
      pin_from: wire.pin_from || null,
      pin_to: wire.pin_to || null,
      color: wire.color
        ? { value: wire.color.id, label: wire.color.name, ...wire.color }
        : null,
      internal: wire.internal || false,
      can_star: wire.can_star || false,
      circuit: wire.circuit
        ? { value: wire.circuit.id, label: wire.circuit.name, ...wire.circuit }
        : null,
      device: wire.device
        ? { value: wire.device.id, label: wire.device.name, ...wire.device }
        : null,
      device_connection: wire.device_connection
        ? { value: wire.device_connection.id, label: wire.device_connection.signal, ...wire.device_connection }
        : null,
      note: wire.note || null,
      level_string: wire.level_string || null,
      terminal_from: wire.terminal_from || null,
      seal_from: wire.seal_from || null,
      terminal_to: wire.terminal_to || null,
      seal_to: wire.seal_to || null,
    }
  }

  useEffect(() => {
    if (!form || !state.show_wire_popup) return
    form.setFieldsValue(getInitialValues())
  }, [form, state.show_wire_popup])

  return (
    <Modal
      open={state.show_wire_popup}
      title={intl.formatMessage({ id: 'wire.wireDetails' })}
      width="80%"
      className="wire-form-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={closeWireFormPopup}
      styles={{ body: { maxHeight: '90vh', overflowY: 'auto' } }}
    >
      <div className="container">
        <Form
          id="wire-form"
          name="wire-form"
          form={form}
          layout="vertical"
          onFinish={saveWire}
        >
          {/* Hidden fields */}
          <InputText name="is_create" hidden={true} isRequired={false} />
          <InputText name="import_file" hidden={true} isRequired={false} />
          {state?.selected_wire?.id && (
            <InputText
              name="id"
              hidden={true}
              isRequired={false}
              value={state?.selected_wire?.id}
            />
          )}

          {/* Section 1: Wire Properties */}
          <Card
            // title={intl.formatMessage({ id: 'wire.sectionWireProperties' })}
            title=''
            variant="outlined"
          >
            <Row gutter={[16, 12]}>
              <Col xs={12} sm={8}>
                <SelectInput
                  name="harness"
                  label={intl.formatMessage({ id: 'wire.harness' })}
                  options={harnessOptions}
                  notFoundContent={
                    searchFailed.harness ? (
                      <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                        {intl.formatMessage({ id: 'generic.searchFailed' })}
                      </Typography.Text>
                    ) : undefined
                  }
                  placeholder={intl.formatMessage({ id: 'wire.selectHarness' })}
                  isRequired={false}
                  allowClear={true}
                  showSearch={true}
                  showLoader={harnessLoading}
                  onInputChange={(value) => {
                    setSearchHarness(value || '')
                  }}
                  formatOption={(option) => (
                    <div className="option-label">{option.label}</div>
                  )}
                  formatLabel={(option) => (
                    <div className="option-label">{option.label}</div>
                  )}
                />
              </Col>

              <Col xs={12} sm={8}>
                <SelectInput
                  name="circuit"
                  label={intl.formatMessage({ id: 'wire.circuit' })}
                  options={circuitOptions}
                  notFoundContent={
                    searchFailed.circuit ? (
                      <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                        {intl.formatMessage({ id: 'generic.searchFailed' })}
                      </Typography.Text>
                    ) : undefined
                  }
                  placeholder={intl.formatMessage({ id: 'wire.selectCircuit' })}
                  isRequired={true}
                  errorHelp={false}
                  allowClear={true}
                  showSearch={true}
                  showLoader={circuitLoading}
                  onInputChange={(value) => {
                    setSearchCircuit(value || '')
                  }}
                />
              </Col>

              <Col xs={12} sm={8}>
                <SelectInput
                  name="color"
                  label={intl.formatMessage({ id: 'wire.color' })}
                  options={colorOptions}
                  notFoundContent={
                    searchFailed.color ? (
                      <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                        {intl.formatMessage({ id: 'generic.searchFailed' })}
                      </Typography.Text>
                    ) : undefined
                  }
                  placeholder={intl.formatMessage({ id: 'wire.selectColor' })}
                  isRequired={false}
                  allowClear={true}
                  showSearch={true}
                  showLoader={colorLoading}
                  onInputChange={(value) => {
                    setSearchColor(value || '')
                  }}
                />
              </Col>

              <Col xs={12} sm={8}>
                <InputText
                  name="size"
                  label={intl.formatMessage({ id: 'wire.size' })}
                  isRequired={true}
                  errorHelp={false}
                />
              </Col>

              <Col xs={12} sm={8} className='checkbox-container'>
                <InlineCheckbox
                  name="internal"
                  label={intl.formatMessage({ id: 'wire.internal' })}
                  isRequired={false}
                  wrapperClass="wire-checkbox"
                />
              </Col>
            </Row>
          </Card>

          {/* Section 2: From / To endpoints */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card
                // title={intl.formatMessage({ id: 'wire.sectionConnectionFrom' })}
                title=''
                variant="outlined"
              >
                <Row gutter={[16, 12]}>
                  <Col xs={12}>
                    <SelectInput
                      name="connector_from"
                      label={intl.formatMessage({ id: 'wire.connectorFrom' })}
                      options={connectorFromOptions}
                      notFoundContent={
                        searchFailed.connector_from ? (
                          <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                            {intl.formatMessage({ id: 'generic.searchFailed' })}
                          </Typography.Text>
                        ) : undefined
                      }
                      placeholder={intl.formatMessage({ id: 'wire.selectConnectorFrom' })}
                      isRequired={true}
                      errorHelp={false}
                      allowClear={true}
                      showSearch={true}
                      showLoader={connectorFromLoading}
                      onInputChange={(value) => {
                        setSearchConnectorFrom(value || '')
                      }}
                      formatOption={(option) => (
                        <div className="option-label">{option.label}</div>
                      )}
                      formatLabel={(option) => (
                        <div className="option-label">{option.label}</div>
                      )}
                    />
                  </Col>

                  <Col xs={12}>
                    <SelectInput
                      name="connector_to"
                      label={intl.formatMessage({ id: 'wire.connectorTo' })}
                      options={connectorToOptions}
                      notFoundContent={
                        searchFailed.connector_to ? (
                          <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                            {intl.formatMessage({ id: 'generic.searchFailed' })}
                          </Typography.Text>
                        ) : undefined
                      }
                      placeholder={intl.formatMessage({ id: 'wire.selectConnectorTo' })}
                      isRequired={true}
                      errorHelp={false}
                      allowClear={true}
                      showSearch={true}
                      showLoader={connectorToLoading}
                      onInputChange={(value) => {
                        setSearchConnectorTo(value || '')
                      }}
                      formatOption={(option) => (
                        <div className="option-label">{option.label}</div>
                      )}
                      formatLabel={(option) => (
                        <div className="option-label">{option.label}</div>
                      )}
                    />
                  </Col>

                  <Col xs={12}>
                    <InputText
                      name="pin_from"
                      label={intl.formatMessage({ id: 'wire.pinFrom' })}
                      isRequired={true}
                      errorHelp={false}
                    />
                  </Col>

                  <Col xs={12}>
                    <InputText
                      name="pin_to"
                      label={intl.formatMessage({ id: 'wire.pinTo' })}
                      isRequired={true}
                      errorHelp={false}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card
                // title={intl.formatMessage({ id: 'wire.sectionConnectionTo' })}
                title=''
                variant="outlined"
              >
                <Row gutter={[16, 12]}>
                  <Col xs={12}>
                    <InputText
                      name="terminal_from"
                      label={intl.formatMessage({ id: 'wire.terminalFrom' })}
                      isRequired={false}
                    />
                  </Col>

                  <Col xs={12}>
                    <InputText
                      name="terminal_to"
                      label={intl.formatMessage({ id: 'wire.terminalTo' })}
                      isRequired={false}
                    />
                  </Col>

                  <Col xs={12}>
                    <InputText
                      name="seal_from"
                      label={intl.formatMessage({ id: 'wire.sealFrom' })}
                      isRequired={false}
                    />
                  </Col>

                  <Col xs={12}>
                    <InputText
                      name="seal_to"
                      label={intl.formatMessage({ id: 'wire.sealTo' })}
                      isRequired={false}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Section 4: Configuration */}
          <Card
            // title={intl.formatMessage({ id: 'wire.sectionConfiguration' })}
            title=''
            variant="outlined"
          >
            <Row gutter={[16, 12]}>
              <Col xs={24} sm={12}>
                <InputText
                  name="option_code_expression"
                  label={intl.formatMessage({ id: 'wire.optionCodeExpression' })}
                  isRequired={false}
                />
              </Col>

              <Col xs={24} sm={12}>
                <TextArea
                  name="level_string"
                  placeholder={intl.formatMessage({ id: 'wire.levelStringPlaceholder' })}
                  label={
                    <span>
                      {intl.formatMessage({ id: 'wire.levelString' })}{' '}
                      <span className="field-label-hint">
                        {intl.formatMessage({ id: 'wire.levelStringHint' })}
                      </span>
                    </span>
                  }
                  isRequired={false}
                  // hint='Here'
                  rows={3}
                />
              </Col>
            </Row>
          </Card>

          {/* Section 5: Notes */}
          <Card
            // title={intl.formatMessage({ id: 'wire.sectionNotes' })}
            title=''
            variant="outlined"
          >
            <Row gutter={[16, 12]}>
              <Col xs={24}>
                <TextArea
                  name="note"
                  label={intl.formatMessage({ id: 'wire.note' })}
                  // hideLabel={true}
                  isRequired={false}
                  rows={3}
                  wrapperClass='notes-field'
                />
              </Col>
            </Row>
          </Card>

          <Flex justify="end">
            <Button htmlType="submit" type="primary">
              {intl.formatMessage({ id: 'wire.saveWire' })}
            </Button>
          </Flex>
        </Form>
      </div>
    </Modal>
  )
}

export default WireFormModal
